import { MRT_ColumnDef } from "../Tanstack-Table/lib";
import { useMediaQuery } from "react-responsive";
import { useEffect, useMemo, useState } from "react";
import { FilterState, InitialTableState, TableData } from "../../types";

const mobileViewColumns: MRT_ColumnDef<TableData>[] = [
  {
    accessorKey:      "shape",
    header:           "SHAPE",
    size:             70,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "b2c_price",
    header:           "PRICE",
    size:             60,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "carat",
    header:           "CARAT",
    size:             45,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "color",
    header:           "COLOR",
    size:             45,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "clarity",
    header:           "CLARITY",
    size:             55,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "type",
    header:           "",
    size:             60,
    isNotShowSorting: true,
  },
];

const tabViewColumns: MRT_ColumnDef<TableData>[] = [
  {
    accessorKey:      "shape",
    header:           "SHAPE",
    size:             165,
    minSize:          190,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "source",
    header:           "TYPE",
    size:             75,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "b2c_price",
    header:           "PRICE",
    size:             75,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "carat",
    header:           "CARAT",
    size:             60,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "color",
    header:           "COLOR",
    size:             52,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "clarity",
    header:           "CLARITY",
    size:             66,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "certification",
    header:           "CERTIFICATION",
    size:             50,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "actions",
    header:           "ACTIONS",
    size:             400,
    minSize:          410,
    isNotShowSorting: true,
  },
  {
    accessorKey:      "type",
    header:           "",
    size:             60,
    isNotShowSorting: true,
  },
];

const desktopViewColumns: MRT_ColumnDef<TableData>[] = [
  {
    accessorKey:      "shape",
    header:           "SHAPE",
    size:             200,
    minSize:          190,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "source",
    header:           "TYPE",
    size:             100,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "b2c_price",
    header:           "PRICE",
    size:             110,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "carat",
    header:           "CARAT",
    size:             80,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "color",
    header:           "COLOR",
    size:             70,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "cut",
    header:           "CUT",
    size:             100,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "clarity",
    header:           "CLARITY",
    size:             85,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "certification",
    header:           "CERTIFICATION",
    size:             60,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "actions",
    header:           "ACTIONS",
    size:             430,
    minSize:          430,
    isNotShowSorting: true,
  },
  {
    accessorKey:      "type",
    header:           "",
    size:             60,
    isNotShowSorting: true,
  },
];

const largeViewColumns: MRT_ColumnDef<TableData>[] = [
  {
    accessorKey:      "shape",
    header:           "SHAPE",
    size:             220,
    minSize:          190,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "source",
    header:           "TYPE",
    size:             110,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "b2c_price",
    header:           "PRICE",
    size:             120,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "carat",
    header:           "CARAT",
    size:             90,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "color",
    header:           "COLOR",
    size:             80,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "cut",
    header:           "CUT",
    size:             110,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "clarity",
    header:           "CLARITY",
    size:             95,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "certification",
    header:           "CERTIFICATION",
    size:             70,
    isNotShowSorting: false,
  },
  {
    accessorKey:      "actions",
    header:           "ACTIONS",
    size:             430,
    minSize:          430,
    isNotShowSorting: true,
  },
  {
    accessorKey:      "type",
    header:           "",
    size:             195,
    isNotShowSorting: true,
  },
];

/* The trailing "type" column exists only to group the Recommended tab. Anywhere else it
   renders as an empty column to the right of ACTIONS which, under table-layout: fixed,
   takes a share of the leftover width - so "View Diamond" sat a different distance from
   the right edge at every screen size. */
const columnsForView = ({
                          isMobileView,
                          isTabView,
                          isLargeView,
                          isGrouping,
                        }: {
  isMobileView: boolean;
  isTabView: boolean;
  isLargeView: boolean;
  isGrouping: boolean;
}): MRT_ColumnDef<TableData>[] => {
  const viewColumns = isMobileView
    ? mobileViewColumns
    : isTabView
      ? tabViewColumns
      : isLargeView
        ? desktopViewColumns
        : largeViewColumns;
  return isGrouping
    ? viewColumns
    : viewColumns.filter((column) => column.accessorKey !== "type");
};

const UseTable = ({
                    data,
                    table,
                    filter,
                    isGrouping,
                  }: {
  data: TableData[];
  table: InitialTableState;
  filter: FilterState;
  isGrouping: boolean;
}) => {
  // If Limited the data needed
  const isMobileView = useMediaQuery({ query: "(max-width: 991px)" });
  const isTabView = useMediaQuery({ query: "(max-width: 1500px)" });
  const isLargeView = useMediaQuery({ query: "(max-width: 2000px)" });
  const [ compareDataIds, setCompareDataIds ] = useState<string[]>([]);
  const [ scrollTop, setScrollTop ] = useState<boolean>(true)
  // If All the data needed
  // const isMobileView = false;
  // const isTabView = false;
  const [ columns, setColumns ] = useState<MRT_ColumnDef<TableData>[]>(
    columnsForView({ isMobileView, isTabView, isLargeView, isGrouping })
  );

  useEffect(() => {
    setColumns(columnsForView({ isMobileView, isTabView, isLargeView, isGrouping }));
  }, [ isMobileView, isTabView, isLargeView, isGrouping ]);

  useEffect(() => {
    const scsData = {
      accessorKey:      "scs",
      header:           "SCS",
      size:             70,
      isNotShowSorting: false,
    };

    if (!isMobileView && !isTabView) {
      if (Object.keys(filter.options).length > 0 && filter.options.source) {
        // Check if source is 'GNU'
        if (filter.options.source === "GNU") {
          // Remove 'scs' column if source is 'GNU'
          const filteredColumns = columns.filter(
            (column) => column.accessorKey !== "sustainable"
          );
          setColumns(filteredColumns);
        }
        /*else {
          // Find 'scs' column
          const scsColumnExists = columns.some(
            (column) => column.accessorKey === "scs"
          );

          if (!scsColumnExists && columns.length > 0) {
            // Find index of 'lab' column
            const labColumnIndex = columns.findIndex(
              (column) => column.accessorKey === "certification"
            );

            if (labColumnIndex !== -1) {
              // Insert 'scs' data after 'lab' column
              const newData = [...columns];
              // newData.splice(labColumnIndex + 1, 0, scsData);
              setColumns(newData);
            }
          }
        }*/
      } else {
        // Set desktop view columns if filter options are empty
        setColumns(columnsForView({ isMobileView, isTabView, isLargeView, isGrouping }));
      }
    }
  }, [ filter.options, isMobileView, isTabView, isLargeView, window.innerWidth ]);

  const tableData = useMemo(() => {
    if (!isMobileView) {
      const activeTab: number =
        table.recentData.length === 0 && table.activeTab === 2
          ? table.activeTab + 1
          : table.activeTab;
      return data.map((item: TableData) => ({
        ...item,
      }));
    } else {
      return data.map((item: any) => ({
        ...item,
        img:   {
          img:   item.img,
          shape: item.shape,
        },
        field: {
          // shape: item.shape,
          carat:   item.carat,
          color:   item.color,
          cut:     item.cut,
          clarity: item.clarity,
        },
        price: item.b2c_price,
      }));
    }
  }, [
    data,
    table.expandRow,
    table.activeTab,
    isMobileView,
    isTabView,
    table.recentData,
  ]);

  useEffect(() => {
    let compareDataList: string[] = [];
    table.compareData.map((item: TableData) => {
      compareDataList = [ ...compareDataList, item.id as string ];
    });
    setCompareDataIds(compareDataList);
  }, [ table ]);

  return {
    isMobileView,
    isTabView,
    columns,
    tableData,
    compareDataIds,
    scrollTop,
    setScrollTop,
  };
};

export default UseTable;
